import { v } from "convex/values";
import type { Jurisdiction } from "./enums";
import { err, ok, type Result } from "./result";
import { cleanRequiredText } from "./text";

export const OBLIGATION_SET_ITEM_STATUSES = [
  "needsReview",
  "covered",
  "partial",
  "missing",
  "unclear",
  "needsLegalReview",
] as const;

export type ObligationSetItemStatus = typeof OBLIGATION_SET_ITEM_STATUSES[number];

export const obligationSetItemStatus = v.union(
  v.literal("needsReview"),
  v.literal("covered"),
  v.literal("partial"),
  v.literal("missing"),
  v.literal("unclear"),
  v.literal("needsLegalReview"),
);

export type ObligationSetItemStatusLabel =
  | "Needs review"
  | "Covered"
  | "Partial"
  | "Missing"
  | "Unclear"
  | "Needs legal review";

const OBLIGATION_SET_STATUS_LABELS = {
  needsReview: "Needs review",
  covered: "Covered",
  partial: "Partial",
  missing: "Missing",
  unclear: "Unclear",
  needsLegalReview: "Needs legal review",
} satisfies Record<ObligationSetItemStatus, ObligationSetItemStatusLabel>;

export const obligationSetItemStatusLabel = (status: ObligationSetItemStatus): ObligationSetItemStatusLabel =>
  OBLIGATION_SET_STATUS_LABELS[status];

export type ObligationSetCreateError =
  | { readonly kind: "nameRequired" }
  | { readonly kind: "itemsRequired" };

export const obligationSetCreateErrorMessage = (error: ObligationSetCreateError): string => {
  switch (error.kind) {
    case "nameRequired":
      return "Name the obligation set before saving.";
    case "itemsRequired":
      return "Select at least one obligation snippet before saving.";
  }
};

export type ObligationSetItemInput<LegislationId extends string, SectionId extends string> = {
  readonly legislationId: LegislationId;
  readonly sectionId: SectionId;
  readonly legislationTitle: string;
  readonly legislationReference: string;
  readonly jurisdiction: Jurisdiction;
  readonly sectionNumber: string;
  readonly sectionTitle: string;
  readonly snippet: string;
  readonly topic: string;
  readonly ownerName: string;
  readonly status: ObligationSetItemStatus;
  readonly evidenceControlReference: string;
  readonly notes: string;
};

export type PreparedObligationSet<CompanyId extends string> = {
  readonly companyId: CompanyId;
  readonly name: string;
  readonly query: string;
  readonly createdAt: number;
  readonly updatedAt: number;
};

export type PreparedObligationSetItem<CompanyId extends string, LegislationId extends string, SectionId extends string> =
  ObligationSetItemInput<LegislationId, SectionId> & {
    readonly companyId: CompanyId;
    readonly order: number;
    readonly createdAt: number;
    readonly updatedAt: number;
  };

export type PreparedObligationSetCreate<CompanyId extends string, LegislationId extends string, SectionId extends string> = {
  readonly set: PreparedObligationSet<CompanyId>;
  readonly items: readonly PreparedObligationSetItem<CompanyId, LegislationId, SectionId>[];
};

export type ObligationSetCreateInput<CompanyId extends string, LegislationId extends string, SectionId extends string> = {
  readonly companyId: CompanyId;
  readonly name: string;
  readonly query: string;
  readonly items: readonly ObligationSetItemInput<LegislationId, SectionId>[];
  readonly now: number;
};

export const obligationSetDefaultName = (query: string): string => {
  const cleaned = query.trim();
  return cleaned === "" ? "Selected obligations" : "Obligations: " + cleaned;
};

export const prepareObligationSetCreate = <CompanyId extends string, LegislationId extends string, SectionId extends string>({
  companyId,
  name,
  query,
  items,
  now,
}: ObligationSetCreateInput<CompanyId, LegislationId, SectionId>): Result<PreparedObligationSetCreate<CompanyId, LegislationId, SectionId>, ObligationSetCreateError> => {
  const cleanedName = cleanRequiredText(name, { kind: "nameRequired" } as const);
  if (!cleanedName.ok) return cleanedName;
  if (items.length === 0) return err({ kind: "itemsRequired" });

  return ok({
    set: {
      companyId,
      name: cleanedName.value,
      query: query.trim(),
      createdAt: now,
      updatedAt: now,
    },
    items: items.map((item, index) => ({
      companyId,
      legislationId: item.legislationId,
      sectionId: item.sectionId,
      legislationTitle: item.legislationTitle.trim(),
      legislationReference: item.legislationReference.trim(),
      jurisdiction: item.jurisdiction,
      sectionNumber: item.sectionNumber.trim(),
      sectionTitle: item.sectionTitle.trim(),
      snippet: item.snippet.trim(),
      topic: item.topic.trim(),
      ownerName: item.ownerName.trim(),
      status: item.status,
      evidenceControlReference: item.evidenceControlReference.trim(),
      notes: item.notes.trim(),
      order: index,
      createdAt: now,
      updatedAt: now,
    })),
  });
};
